import React, { useState, useEffect, useMemo, useContext } from 'react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import AppContext from '../../contexts/AppContext';
import { hasRole, SUPERADMIN_MANAGERS } from '../../utils/roleUtils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Users, UserPlus, Settings, Edit, Trash2, Filter, FilterX, Upload, RotateCcw, UserX, UserCheck, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { UserCreateDialog } from './components/UserCreateDialog';
import { UserEditDialog } from './components/UserEditDialog';
import { UserDeleteDialog } from './components/UserDeleteDialog';
import { UserImportDialog } from './components/UserImportDialog';
import { DataTable } from '../../components/DataTable';
import { AdminDashboardSkeleton } from './components/AdminDashboardSkeleton';

const AdminDashboard = () => {
  const { currentUser } = useContext(AppContext);
  const isSuperAdmin = hasRole(currentUser?.role, 'superadmin');

  const canEditTarget = (targetRole) => {
    if (!hasRole(currentUser?.role, 'admin+')) return false;
    if (hasRole(targetRole, 'superadmin')) {
      return isSuperAdmin && SUPERADMIN_MANAGERS.includes(currentUser?.email);
    }
    if (hasRole(targetRole, 'admin+')) return isSuperAdmin;
    return true;
  };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [dialogState, setDialogState] = useState({
    create: false,
    edit: false,
    delete: false,
    import: false,
    bulkDeactivate: false,
    bulkReactivate: false,
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      const minDelay = new Promise(resolve => setTimeout(resolve, 600));
      const [response] = await Promise.all([fetchWithAuth('/users'), minDelay]);

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const json = await response.json();

      if (json.success) {
        setUsers(json.data);
      } else {
        throw new Error(json.error || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreateUser = () => {
    setDialogState({ create: true, edit: false, delete: false, import: false });
  };

  const handleImportUsers = () => {
    setDialogState({ create: false, edit: false, delete: false, import: true });
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setDialogState({ create: false, edit: true, delete: false, import: false });
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDialogState({ create: false, edit: false, delete: true, import: false });
  };

  const handleCloseDialogs = () => {
    setDialogState({ create: false, edit: false, delete: false, import: false, bulkDeactivate: false, bulkReactivate: false });
    setSelectedUser(null);
  };

  const handleUserCreated = () => {
    loadUsers();
    handleCloseDialogs();
  };

  const handleUserUpdated = () => {
    loadUsers();
    handleCloseDialogs();
  };

  const handleUserDeleted = () => {
    loadUsers();
    handleCloseDialogs();
  };

  const handleReactivateUser = async (user) => {
    try {
      const response = await fetchWithAuth(`/users/${user.id}/reactivate`, { method: 'PATCH' });
      const json = await response.json();
      if (json.success) {
        loadUsers();
      }
    } catch (err) {
      console.error('Error reactivating user:', err);
    }
  };

  const handleBulkDeactivate = () => {
    setDialogState(prev => ({ ...prev, bulkDeactivate: true }));
  };

  const handleBulkReactivate = () => {
    setDialogState(prev => ({ ...prev, bulkReactivate: true }));
  };

  const handleBulkDeactivateConfirmed = async () => {
    const selectedUserIds = getSelectedUsers().filter(u => u.isActive !== false).map(u => u.id);

    try {
      const response = await fetchWithAuth('/users/bulk-deactivate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedUserIds }),
      });

      const json = await response.json();
      if (json.success) {
        setRowSelection({});
        loadUsers();
      }
    } catch (err) {
      console.error('Error bulk deactivating users:', err);
    } finally {
      setDialogState(prev => ({ ...prev, bulkDeactivate: false }));
    }
  };

  const handleBulkReactivateConfirmed = async () => {
    const selectedUserIds = getSelectedUsers().filter(u => u.isActive === false).map(u => u.id);

    try {
      const response = await fetchWithAuth('/users/bulk-reactivate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedUserIds }),
      });

      const json = await response.json();
      if (json.success) {
        setRowSelection({});
        loadUsers();
      }
    } catch (err) {
      console.error('Error bulk reactivating users:', err);
    } finally {
      setDialogState(prev => ({ ...prev, bulkReactivate: false }));
    }
  };

  // Get selected users info for the dialogs
  const getSelectedUsers = () => {
    return Object.keys(rowSelection).map(idx => users[parseInt(idx)]).filter(Boolean);
  };

  // Format role for display and search
  const formatRole = (role) => {
    const roleConfig = {
      superadmin: 'Super Admin',
      admin: 'Admin',
      manager: 'Manager',
      technician: 'Technicien',
      user: 'Utilisateur'
    };
    return roleConfig[role] || roleConfig.user;
  };

  // Get unique service desks
  const uniqueServiceDesks = useMemo(() => {
    const desksMap = new Map();
    users.forEach(user => {
      const id = Array.isArray(user.serviceDeskId)
        ? user.serviceDeskId.join(',')
        : String(user.serviceDeskId);
      if (!desksMap.has(id)) {
        desksMap.set(id, {
          id,
          name: user.serviceDeskName
        });
      }
    });
    return Array.from(desksMap.values());
  }, [users]);

  // Custom filters render function
  const renderCustomFilters = ({ table, columnFilters }) => {
    const roleFilter = columnFilters.find(f => f.id === 'role')?.value || 'all';
    const serviceDeskFilter = columnFilters.find(f => f.id === 'serviceDeskName')?.value || 'all';
    const statusFilter = columnFilters.find(f => f.id === 'status')?.value || 'all';
    const hasActiveFilters = roleFilter !== 'all' || (isSuperAdmin && serviceDeskFilter !== 'all') || statusFilter !== 'all';

    return (
      <>
        <Select
          value={roleFilter}
          onValueChange={(value) => {
            table.getColumn('role')?.setFilterValue(value === 'all' ? undefined : value);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tous les rôles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les rôles</SelectItem>
            <SelectItem value="Super Admin">Super Admin</SelectItem>
            <SelectItem value="Admin">Admin</SelectItem>
            <SelectItem value="Manager">Manager</SelectItem>
            <SelectItem value="Technicien">Technicien</SelectItem>
            <SelectItem value="Utilisateur">Utilisateur</SelectItem>
          </SelectContent>
        </Select>

        {isSuperAdmin && (
          <Select
            value={serviceDeskFilter}
            onValueChange={(value) => {
              table.getColumn('serviceDeskName')?.setFilterValue(value === 'all' ? undefined : value);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Tous les service desks" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les service desks</SelectItem>
              {uniqueServiceDesks.map((desk) => (
                <SelectItem key={desk.id} value={desk.name}>
                  {desk.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select
          value={statusFilter}
          onValueChange={(value) => {
            table.getColumn('status')?.setFilterValue(value === 'all' ? undefined : value);
          }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Tous les statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="Actif">Actif</SelectItem>
            <SelectItem value="Inactif">Inactif</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.resetColumnFilters()}
            className="gap-2"
          >
            <FilterX className="h-4 w-4" />
            Effacer les filtres
          </Button>
        )}
      </>
    );
  };

  // Table columns definition for TanStack Table
  const columns = useMemo(
    () => [
      {
        accessorKey: 'username',
        header: 'Nom d\'utilisateur',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className={row.original.isActive === false ? 'text-gray-400 line-through' : ''}>
              {row.original.username}
            </span>
            {row.original.isActive === false && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500">Inactif</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: true,
      },
      {
        accessorFn: (row) => formatRole(row.role),
        id: 'role',
        header: 'Rôle',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'includesString',
        cell: ({ row }) => {
          const roleConfig = {
            superadmin: { label: 'Super Admin', className: 'bg-red-100 text-red-800' },
            admin: { label: 'Admin', className: 'bg-red-100 text-red-800' },
            manager: { label: 'Manager', className: 'bg-red-100 text-red-800' },
            technician: { label: 'Technicien', className: 'bg-gray-100 text-gray-800' },
            user: { label: 'Utilisateur', className: 'bg-blue-100 text-blue-800' }
          };
          const config = roleConfig[row.original.role] || roleConfig.user;
          return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
              {config.label}
            </span>
          );
        },
      },
      {
        accessorFn: (row) => row.serviceDeskName,
        id: 'serviceDeskName',
        header: 'Service Desk',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.serviceDeskName}</span>
            <span className="text-xs text-gray-500">
              ID: {Array.isArray(row.original.serviceDeskId)
                ? row.original.serviceDeskId.join(', ')
                : row.original.serviceDeskId}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'mode',
        header: 'Mode',
        enableSorting: true,
        cell: ({ row }) => (
          <span className={`px-2 py-1 rounded-full text-xs ${
            row.original.mode === 'rw' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {row.original.mode === 'rw' ? 'Lecture / Écriture' : 'Lecture seule'}
          </span>
        ),
      },
      {
        accessorFn: (row) => {
          if (row.sectors && row.sectors.length > 0) {
            return row.sectors.join(', ');
          }
          return '-';
        },
        id: 'sectors',
        header: 'Secteurs',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="max-w-xs truncate" title={row.original.sectors?.join(', ')}>
            {row.original.sectors && row.original.sectors.length > 0 ? row.original.sectors.join(', ') : '-'}
          </div>
        ),
      },
      {
        accessorFn: (row) => row.isActive === false ? 'Inactif' : 'Actif',
        id: 'status',
        header: 'Statut',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        meta: { hidden: true },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          if (!canEditTarget(row.original.role)) return null;
          return (
          <div className="flex gap-2">
            <button
              onClick={() => handleEditUser(row.original)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Modifier"
            >
              <Edit className="w-4 h-4" />
            </button>
            {row.original.isActive === false ? (
              <button
                onClick={() => handleReactivateUser(row.original)}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                title="Réactiver"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => handleDeleteUser(row.original)}
                className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                title="Désactiver"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        );
        },
      },
    ],
    [handleEditUser, handleDeleteUser, handleReactivateUser, canEditTarget]
  );

  // Calculate statistics
  const activeUsers = users.filter(u => u.isActive !== false);
  const stats = {
    total: users.length,
    active: activeUsers.length,
    inactive: users.filter(u => u.isActive === false).length,
    admins: activeUsers.filter(u => u.role === 'superadmin' || u.role === 'admin').length,
    technicians: activeUsers.filter(u => u.role === 'technician').length,
    users: activeUsers.filter(u => u.role === 'user').length
  };

  if (loading && users.length === 0) {
    return <AdminDashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
        <h3 className="font-bold">Une erreur est survenue</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-8 h-8" />
            Users
          </h1>
          <p className="text-gray-500 mt-1">Add or remove users</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleImportUsers} size="lg" variant="outline">
            <Upload className="w-4 h-4 mr-2" />
            Importer des utilisateurs
          </Button>
          <Button onClick={handleCreateUser} size="lg" className="bg-black hover:bg-gray-800">
            <UserPlus className="w-4 h-4 mr-2" />
            Créer un utilisateur
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Utilisateurs</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.active} actifs, {stats.inactive} inactifs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Administrateurs</CardTitle>
            <Badge variant="destructive" className="h-4 px-2">Admin</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.admins}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Comptes avec privilèges admin
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Techniciens</CardTitle>
            <Badge variant="secondary" className="h-4 px-2">Tech</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.technicians}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Support technique
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utilisateurs</CardTitle>
            <Badge variant="outline" className="h-4 px-2">User</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.users}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Utilisateurs standards
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Gestion des utilisateurs</CardTitle>
          <CardDescription>
            Visualisez, créez, modifiez ou désactivez des comptes utilisateurs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={users}
            columns={columns}
            emptyMessage="Aucun utilisateur trouvé"
            showGlobalSearch={true}
            showPagination={true}
            pageSize={50}
            customFilters={renderCustomFilters}
            enableRowSelection={true}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            canSelectRow={(user) => canEditTarget(user.role)}
            bulkActionsBar={() => {
              const selected = getSelectedUsers();
              const activeCount = selected.filter(u => u.isActive !== false).length;
              const inactiveCount = selected.filter(u => u.isActive === false).length;
              return (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                  <span className="text-sm font-medium text-blue-800">
                    {selected.length} utilisateur{selected.length > 1 ? 's' : ''} sélectionné{selected.length > 1 ? 's' : ''}
                    {activeCount > 0 && inactiveCount > 0 && (
                      <span className="text-blue-600 ml-1">
                        ({activeCount} actif{activeCount > 1 ? 's' : ''}, {inactiveCount} inactif{inactiveCount > 1 ? 's' : ''})
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRowSelection({})}
                      className="gap-1"
                    >
                      <X className="h-3 w-3" />
                      Désélectionner
                    </Button>
                    {activeCount > 0 && (
                      <Button
                        size="sm"
                        onClick={handleBulkDeactivate}
                        className="gap-1 bg-amber-600 hover:bg-amber-700"
                      >
                        <UserX className="h-3 w-3" />
                        Désactiver ({activeCount})
                      </Button>
                    )}
                    {inactiveCount > 0 && (
                      <Button
                        size="sm"
                        onClick={handleBulkReactivate}
                        className="gap-1 bg-green-600 hover:bg-green-700"
                      >
                        <UserCheck className="h-3 w-3" />
                        Réactiver ({inactiveCount})
                      </Button>
                    )}
                  </div>
                </div>
              );
            }}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <UserCreateDialog
        open={dialogState.create}
        onClose={handleCloseDialogs}
        onUserCreated={handleUserCreated}
      />

      <UserEditDialog
        open={dialogState.edit}
        user={selectedUser}
        onClose={handleCloseDialogs}
        onUserUpdated={handleUserUpdated}
      />

      <UserDeleteDialog
        open={dialogState.delete}
        user={selectedUser}
        onClose={handleCloseDialogs}
        onUserDeleted={handleUserDeleted}
      />

      <UserImportDialog
        open={dialogState.import}
        onClose={handleCloseDialogs}
        onUsersImported={loadUsers}
      />

      {/* Bulk Deactivate Dialog */}
      <AlertDialog open={dialogState.bulkDeactivate} onOpenChange={() => setDialogState(prev => ({ ...prev, bulkDeactivate: false }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-amber-600" />
              <AlertDialogTitle>Désactivation en masse</AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  Êtes-vous sûr de vouloir désactiver <strong>{getSelectedUsers().length} utilisateur{getSelectedUsers().length > 1 ? 's' : ''}</strong> ?
                </p>
                <ul className="mt-3 max-h-40 overflow-y-auto space-y-1 text-sm">
                  {getSelectedUsers().map(user => (
                    <li key={user.id} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      {user.username} ({user.email})
                    </li>
                  ))}
                </ul>
                <p className="mt-3">
                  Ces utilisateurs ne pourront plus se connecter. Vous pourrez les réactiver ultérieurement.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDeactivateConfirmed}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Désactiver ({getSelectedUsers().length})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Bulk Reactivate Dialog */}
      <AlertDialog open={dialogState.bulkReactivate} onOpenChange={() => setDialogState(prev => ({ ...prev, bulkReactivate: false }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-green-600" />
              <AlertDialogTitle>Réactivation en masse</AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  Êtes-vous sûr de vouloir réactiver <strong>{getSelectedUsers().filter(u => u.isActive === false).length} utilisateur{getSelectedUsers().filter(u => u.isActive === false).length > 1 ? 's' : ''}</strong> ?
                </p>
                <ul className="mt-3 max-h-40 overflow-y-auto space-y-1 text-sm">
                  {getSelectedUsers().filter(u => u.isActive === false).map(user => (
                    <li key={user.id} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      {user.username} ({user.email})
                    </li>
                  ))}
                </ul>
                <p className="mt-3">
                  Ces utilisateurs pourront à nouveau se connecter.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkReactivateConfirmed}
              className="bg-green-600 hover:bg-green-700"
            >
              Réactiver ({getSelectedUsers().filter(u => u.isActive === false).length})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
